// Catalogus van dibbi-rondleidingen (walkthroughs).
//
// Rondleidingen zijn 16:9 uitlegvideo's die los worden geproduceerd en
// geserveerd op https://vibbo.nl/w/<token>. Dit bestand is de bron voor de
// admin-keuzelijst ("Rondleiding koppelen") en voor het tonen van de juiste
// speler in de kennisbank. Een artikel verwijst via chat_knowledge_base.
// walkthrough_token naar een van deze tokens.
//
// Bron van de tokens: Cloudflare D1 `opname-welcome` tabel `walkthrough`,
// dubbel geverifieerd tegen de dibbi.nl/uitleg-embedpagina's. De twee oude
// tokenloze concepten (dibbi-aan-de-slag, dibbi-onboarding) staan hier
// bewust niet in: die hebben geen live speler-link.

export type WalkthroughCategory =
  | 'campaigns'
  | 'blogs'
  | 'mailinglist'
  | 'social-media'
  | 'account'
  | 'credits';

export interface Walkthrough {
  /** Stabiele identifier, gelijk aan de bron-slug. */
  slug: string;
  /** Weergavetitel zoals getoond in de keuzelijst en de speler. */
  title: string;
  /** Onraadbaar kijk-token achter https://vibbo.nl/w/<token>. */
  token: string;
  /** Duur in seconden (indicatief). */
  duration: number;
  /** Best passende kennisbank-categorie, als hint bij het koppelen. */
  suggestedCategory: WalkthroughCategory;
}

export const WALKTHROUGH_BASE_URL = 'https://vibbo.nl/w';

export const WALKTHROUGHS: Walkthrough[] = [
  {
    slug: 'aan-de-slag',
    title: 'Aan de slag',
    token: 'eyDaq115nnF4VrKcmmgXcxtRs-F43hJ05gHCkxzjJJ8',
    duration: 42.88,
    suggestedCategory: 'account',
  },
  {
    slug: 'blog-share-mail',
    title: 'Je blog delen via e-mail',
    token: 'jPQLcYBMoAfYQR6gwXDnm2dXM0TRb90vTQ3Ns9iOATc',
    duration: 98.08,
    suggestedCategory: 'campaigns',
  },
  {
    slug: 'blog-to-social',
    title: 'Van blog naar social',
    token: 'SH27MN9LpzpkxBm9e2dASh8iejXaAOe56pbOMNpd6cI',
    duration: 51.84,
    suggestedCategory: 'social-media',
  },
  {
    slug: 'blogwizard',
    title: 'Van idee tot online',
    token: 'qwLgQXuOYosd3NNGCxfWT5WbV290XxxUH-es2dpk69o',
    duration: 77.52,
    suggestedCategory: 'blogs',
  },
  {
    slug: 'contacten',
    title: 'Je contacten op één plek',
    token: '10ehNAi_UitQ81OjD-djuAAuRJZxugEtKm0iB0IIf8Q',
    duration: 51.44,
    suggestedCategory: 'mailinglist',
  },
  {
    slug: 'magic-link',
    title: 'Eenvoudig en veilig inloggen',
    token: 'hA9axf5rTTYfTWi2fbOLNLWsECoNmsN33OLHXHyUXoU',
    duration: 49.76,
    suggestedCategory: 'account',
  },
  {
    slug: 'nieuwsbrief',
    title: 'Je nieuwsbrief versturen',
    token: 'erhGBBxEw5isfBGcCojOwY3C844EGCCUcSUSaBiGw44',
    duration: 41.04,
    suggestedCategory: 'campaigns',
  },
  {
    slug: 'online',
    title: 'Je blog automatisch online',
    token: 'VvAEiNBK5szcslkdpRAcXp9p39Sqq3TwSimwlQAOXZ8',
    duration: 52.08,
    suggestedCategory: 'blogs',
  },
  {
    slug: 'planning',
    title: 'Houd je ritme',
    token: 'Zi0oL4kKTV4J753sxQb3vbqf6dT9XZSrIYP39lvCpwY',
    duration: 35.6,
    suggestedCategory: 'campaigns',
  },
  {
    slug: 'social-koppelen',
    title: 'Je social kanalen koppelen',
    token: 'Nondq7cRUkRnvic1AfTFqMBLT0jWcrq2482tGNPpZ-4',
    duration: 40.56,
    suggestedCategory: 'social-media',
  },
  {
    slug: 'support',
    title: 'Zo krijg je support',
    token: 'OCDi9kMChix9ro6ydB7Mh3-u_x3-b0STNjXjMmSRrbQ',
    duration: 36.16,
    suggestedCategory: 'account',
  },
  {
    slug: 'tone-of-voice',
    title: 'Klinkt als jij',
    token: 'OU_L2xPMEPIS3twnRuxtLetYdlFqO384YchSVrrtnl4',
    duration: 39.68,
    suggestedCategory: 'blogs',
  },
  {
    slug: 'whatsapp-idee',
    title: 'Stuur je idee gewoon een appje',
    token: 'QiZz7-Kh2c3OLhhUkVzRsFCA9p830j4muHc4fvP1Q7k',
    duration: 42.88,
    suggestedCategory: 'blogs',
  },
  {
    slug: 'wordpress',
    title: 'Je blogs op je WordPress-website',
    token: 'H6VetkXb-xE6KmcPMGfWOIN7sbNywRtDz9uIUpqsh1k',
    duration: 56.72,
    suggestedCategory: 'blogs',
  },
];

/** Volledige speler-URL voor een token. */
export function walkthroughUrl(token: string): string {
  return `${WALKTHROUGH_BASE_URL}/${token}`;
}

/** Poster-thumbnail URL voor een token (16:9, image/png). */
export function walkthroughPoster(token: string): string {
  return `${WALKTHROUGH_BASE_URL}/${token}/poster.png`;
}

/** Zoek een rondleiding op token (bijv. om de titel te tonen). */
export function findWalkthroughByToken(token: string): Walkthrough | undefined {
  return WALKTHROUGHS.find((w) => w.token === token);
}
